import PropTypes, { bool, node } from "prop-types";
import React, { forwardRef } from "react";

import styled from "styled-components/macro";

import { Input, Label } from "./Styles";

export const StreetField = forwardRef(function StreetField(
  {
    value = "",
    onChange,
    onBlur,
    label = "Address",
    labelBadge,
    name = "street",
    placeholder = "Street",
    hideInternalFocusStyles = false,
    autoComplete = "off",
    style,
    disabled,
  },
  ref
) {
  return (
    <Wrapper style={style}>
      {/* With a badge supplied (e.g. the USPS-Verified pill) seat it inline
          with the label on a center-aligned row, so it sits beside the title
          rather than stacking above the input. With none, the label keeps its
          plain block layout — no reserved gap when there's nothing to show. */}
      {labelBadge ? (
        <LabelRow>
          <Label htmlFor={name}>{label}</Label>
          <BadgeSlot>{labelBadge}</BadgeSlot>
        </LabelRow>
      ) : (
        <Label htmlFor={name}>{label}</Label>
      )}
      <Input
        ref={ref}
        id={name}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        hideInternalFocusStyles={hideInternalFocusStyles}
        autoComplete={autoComplete}
        disabled={disabled}
      />
    </Wrapper>
  );
});

StreetField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  label: PropTypes.string,
  // Optional node rendered inline, center-aligned, to the right of the label.
  labelBadge: node,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  autoComplete: PropTypes.oneOf(["on", "off"]),
  style: PropTypes.object,
  disabled: bool,
};

const Wrapper = styled.div`
  flex-grow: 1;
`;

// Seats the label and its badge on one center-aligned row. The label's own
// 2px left margin and the badge family's dimensions carry the spacing; the
// small gap keeps the pill off the label glyphs. The 2px bottom margin keeps
// the badge pill off the input's top border (the plain-label case needs none —
// text descenders don't crowd the border the way the bordered pill does).
const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 2px;
`;

// The badge's opacity-toggle wrapper is a block whose line box baseline-parks
// the inline pill ~2px low. Flex that wrapper (its direct child) so the pill
// centers within it, then center the wrapper against the label — the pill lands
// exactly on the label's center line.
const BadgeSlot = styled.span`
  display: inline-flex;
  align-items: center;
  & > * {
    display: flex;
    align-items: center;
  }
`;
